import { getDeviceInfo } from '../../Upshow';
import TrackerService from '../TrackerService';
import { CHECK_STATUS, OVERALL_STATUS, computeOverallStatus } from './types';
import * as drmCheck from './checks/drmLicenseReachability';
import * as manifestCheck from './checks/manifestCdnAccess';
import * as speedCheck from './checks/connectionSpeed';
import * as qualityCheck from './checks/connectionQuality';
import * as domainCheck from './checks/portDomainReachability';
import * as socketCheck from './checks/socketReachability';

const ALL_CHECKS = [drmCheck, manifestCheck, speedCheck, qualityCheck, domainCheck, socketCheck];

// 5s above the slowest legit check (speed ~30s, quality ~12s). Prevents any hung
// promise from blocking the loading screen forever. Mirrors the check-level guard in
// connectionSpeed.js but acts as last-resort defense for all current and future checks.
const CHECK_TIMEOUT_MS = 35000;

// Resolves with onTimeout() result if promise doesn't settle within ms; re-throws on rejection.
function withTimeout(promise, ms, onTimeout) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => resolve(onTimeout()), ms);
    promise.then(
      (v) => { clearTimeout(t); resolve(v); },
      (e) => { clearTimeout(t); reject(e); }
    );
  });
}

class ReadinessService {
  constructor() {
    this._result = null;
  }

  async runBootChecks(onUpdate) {
    // QA preview flag — ?rkmock=fail|atrisk|ready (or bare ?rkmock → fail)
    if (window.rkMock != null) {
      const mode = window.rkMock || 'fail';
      const status = { ready: CHECK_STATUS.PASS, atrisk: CHECK_STATUS.AT_RISK }[mode] ?? CHECK_STATUS.FAIL;
      const FAIL_FIXTURES = {
        drmLicenseReachability: { detail: 'DRM endpoints unreachable',                detailData: { uplynk: 'failed', ezdrm: 'failed' } },
        manifestCdnAccess:      { detail: 'Manifest fetch failed',                     detailData: { httpStatus: 503, hasExtM3U: false } },
        connectionSpeed:        { detail: '2.1 Mbps — below minimum',                 detailData: { measuredMbps: 2.1, tier: 'below_essential' } },
        connectionQuality:      { detail: 'avg 620 ms — high latency',                detailData: { avgLatencyMs: 620, samples: 5 } },
        portDomainReachability: { detail: '2 critical domain(s) unreachable', detailData: { totalCount: 42, unresolvedCount: 2, wildcardSkipped: 0, deviceScopedOut: 0, deviceType: 'chromeos',
          criticalUnreachable: ['stream.upshow.tv', 'content.uplynk.com'], nonCriticalUnreachable: [] } },
        socketReachability:     { detail: 'WebSocket server unreachable: net::ERR_CONNECTION_REFUSED', detailData: { httpStatus: null } },
      };
      const AT_RISK_FIXTURES = {
        drmLicenseReachability: { detail: 'EZDRM endpoint degraded',                  detailData: { uplynk: 'ok', ezdrm: 'degraded' } },
        manifestCdnAccess:      { detail: 'Manifest CDN elevated response time',       detailData: { httpStatus: 200, hasExtM3U: true } },
        connectionSpeed:        { detail: '4.2 Mbps — essential streams only',         detailData: { measuredMbps: 4.2, tier: 'essential_only' } },
        connectionQuality:      { detail: 'avg 350 ms — elevated latency',            detailData: { avgLatencyMs: 350, samples: 5 } },
        portDomainReachability: { detail: '3 non-critical domain(s) unreachable', detailData: { totalCount: 42, unresolvedCount: 3, wildcardSkipped: 0, deviceScopedOut: 0, deviceType: 'chromeos',
          criticalUnreachable: [], nonCriticalUnreachable: ['google-analytics.com', 'data.pendo.io', 'googletagmanager.com'] } },
        socketReachability:     { detail: 'WebSocket server returned HTTP 503', detailData: { httpStatus: 503, body: 'Service Unavailable' } },
      };
      const FIXTURES = mode === 'atrisk' ? AT_RISK_FIXTURES : FAIL_FIXTURES;
      const results = ALL_CHECKS.map((c, i) => {
        const fx = mode !== 'ready' ? (FIXTURES[c.id] ?? { detail: 'Mock failure', detailData: {} }) : { detail: null, detailData: {} };
        return { id: c.id ?? `check_${i}`, label: c.label ?? `Check ${i}`, status, ...fx, durationMs: 0 };
      });
      if (onUpdate) onUpdate([...results]);
      const result = {
        deviceId: 'rkmock',
        deviceType: 'chromeos',
        timestamp: new Date().toISOString(),
        overallStatus: computeOverallStatus(results),
        checks: Object.fromEntries(results.map(r => [r.id, r])),
      };
      this._result = result;
      return result;
    }

    try {
      const deviceInfo = getDeviceInfo();
      const ctx = {
        deviceType: deviceInfo.model?.toLowerCase() ?? 'webview',
        serial: deviceInfo.serial,
      };

      // Seed the UI with all checks in RUNNING state immediately.
      // This array is mutated in-place as checks resolve so onUpdate always
      // delivers results in the fixed ALL_CHECKS order.
      const results = ALL_CHECKS.map((check, i) => ({
        id: check.id ?? `check_${i}`,
        label: check.label ?? `Check ${i}`,
        status: CHECK_STATUS.RUNNING,
        detail: null,
        durationMs: null,
      }));
      if (onUpdate) onUpdate([...results]);

      const makeRunner = (check, i) => {
        const id = check.id ?? `check_${i}`;
        const label = check.label ?? `Check ${i}`;
        return withTimeout(
          check.run(ctx),
          CHECK_TIMEOUT_MS,
          () => ({
            id,
            label,
            status: CHECK_STATUS.FAIL,
            detail: 'Check timed out after 35s',
            detailData: {},
            durationMs: CHECK_TIMEOUT_MS,
          })
        ).then(
          (result) => {
            results[i] = result;
            if (onUpdate) onUpdate([...results]);
            return result;
          },
          (reason) => {
            results[i] = {
              id,
              label,
              status: CHECK_STATUS.FAIL,
              detail: `Check threw: ${reason?.message ?? reason}`,
            };
            if (onUpdate) onUpdate([...results]);
            return results[i];
          }
        );
      };

      // All runners created upfront so every withTimeout setTimeout is registered before any
      // await — required for fake-timer tests and ensures no check is silently skipped.
      const runners = ALL_CHECKS.map((check, i) => makeRunner(check, i));

      // Stage 1: connectionSpeed alone — 10 MB download needs uncontested bandwidth so the
      // measurement isn't skewed by concurrent DRM/manifest/domain fetches or quality pings.
      const speedIdx = ALL_CHECKS.findIndex(c => c.id === 'connectionSpeed');
      await runners[speedIdx];

      // Stage 2: remaining checks in parallel — all are light (tiny GETs / small pings /
      // no-cors connects) and don't meaningfully interfere with each other.
      await Promise.all(runners.filter((_, i) => i !== speedIdx));

      const checksMap = {};
      for (const r of results) checksMap[r.id] = r;

      const result = {
        deviceId: deviceInfo.serial,
        deviceType: ctx.deviceType,
        timestamp: new Date().toISOString(),
        overallStatus: computeOverallStatus(results),
        checks: checksMap,
      };

      this._result = result;

      try {
        TrackerService.appEvents.logMetric({ action: 'boot_test', bootResultObject: result });
      } catch (trackErr) {
        // TrackerService may not be initialized yet — log but don't fail
        console.error('[ReadinessService] Failed to log metric:', trackErr);
      }

      return result;
    } catch (err) {
      const syntheticResult = {
        deviceId: null,
        deviceType: null,
        timestamp: new Date().toISOString(),
        overallStatus: OVERALL_STATUS.NOT_READY,
        checks: {
          runnerError: {
            id: 'runnerError',
            label: 'Boot Runner',
            status: CHECK_STATUS.FAIL,
            detail: `Boot check runner failed catastrophically: ${err?.message ?? err}`,
          },
        },
      };
      this._result = syntheticResult;
      return syntheticResult;
    }
  }

  getLastResult() {
    return this._result;
  }
}

export default new ReadinessService();
